import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of, switchMap } from 'rxjs';
import { AccountingAccountService } from '../../../accounting/service/data/accounting-account.service';
import { AccountRoutes } from '../../../common/model/account-definition';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { AutoCompleteSelectionState } from '../../../core/component/autocomplete-remote/autocomplete-service.interface';
import { RowClickEvent } from '../../../core/component/list/list-view.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ExpandedListViewItemDefinition } from '../../../core/model/expanded-list-view-item';
import { ExpandedListViewRow } from '../../../core/model/list-view-row';
import { LocalDate } from '../../../core/model/local-date';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { InvoiceService } from '../../../invoice/service/data/invoice.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { ReportService } from '../../service/data/report.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels, defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-account-balance-per-invoice',
    templateUrl: './account-balance-per-invoice.component.html',
    styleUrls: ['./account-balance-per-invoice.component.scss'],
    providers: [AccountingAccountService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountBalancePerInvoiceComponent implements OnInit, IAutoCompleteService {
  protected sums: {
    accountBalanceCurrency?: string | null;
    accountBalanceSum?: number;
    isAccountBalanceSumAvailable?: boolean;
    debitCurrency?: string | null;
    debitSum?: number;
    isDebitSumAvailable?: boolean;
    creditCurrency?: string | null;
    creditSum?: number;
    isCreditSumAvailable?: boolean;
  } = {};

  summaryBlocks = [
    { label: 'ACCOUNT_BALANCE.DEBIT_SUM', value: 'debitSum', available: 'isDebitSumAvailable', currency: 'debitCurrency' },
    { label: 'ACCOUNT_BALANCE.CREDIT_SUM', value: 'creditSum', available: 'isCreditSumAvailable', currency: 'creditCurrency' },
    {
      label: 'ACCOUNT_BALANCE.ACCOUNT_BALANCE_SUM',
      value: 'accountBalanceSum',
      available: 'isAccountBalanceSumAvailable',
      currency: 'accountBalanceCurrency',
    },
  ];

  constructor(
    private readonly _cdf: ColumnDefinitionFactory,
    private readonly _companyService: CompanyService,
    private readonly _accountService: AccountingAccountService,
    private readonly _dataService: ReportService,
    private readonly _invoiceService: InvoiceService,
    private readonly _navigationService: NavigationService,
    private readonly _nativeWindow: NativeWindowService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    fb: FormBuilder,
    fcf: FormControlExFactory
  ) {
    let defaultDate = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().firstDayInMonth()).addDays(-1));

    this.form = fb.group({
      companyId: fcf.createMandatory('COMMON.COMPANY', null),
      reconciliationDate: fcf.createMandatory('REPORT.RECONCILIATION_DATE', defaultDate),
      accountId: fcf.createMandatory('COMPANY.BANK_ACCOUNT.ACCOUNTING_ACCOUNT', null),
      ignoreRounding: fcf.create('REPORT.ACCOUNT_BALANCE.IGNORE_ROUNDING', true),
    });

    this.form.controls['companyId'].valueChanges.pipe(switchMap(id => this._companyService.get(id))).subscribe(company => {
      if (company.accountPlanId === this.accountPlanId) return;
      this.accountPlanId = company.accountPlanId;
      this.form.controls['accountId'].setValue(null);
    });

    this.columnDefinitions = [
      this._cdf.create('INVOICE.DEBTOR', 'debtorOneContact'),
      this._cdf.createDate('INVOICE.DUE_DATE', 'dueDate'),
      this._cdf.create('INVOICE.PERIOD', 'refersToPeriod'),
      this._cdf.createMoney('ACCOUNT.DEBIT', 'debit'),
      this._cdf.createMoney('ACCOUNT.CREDIT', 'credit'),
      this._cdf.createMoney('REPORT.ACCOUNT_BALANCE', 'balance'),
    ];
  }

  private accountPlanId: string;
  private dimensionLabels: DimensionLabels = defaultDimensionLabels;
  get accountAutoCompleteService(): IAutoCompleteService {
    return this;
  }

  getSelectionItem(id: string, state: AutoCompleteSelectionState<any, any>): Observable<AutoCompleteSelectionItem<any>> {
    return this._accountService.getSelectionItem(id);
  }
  getSelection(filter: string, options: any, page?: number): Observable<AutoCompleteSelectionItem<any>[]> {
    if (!this.accountPlanId) return of([]);
    return this._accountService.getSelection(filter, this.accountPlanId, page);
  }

  columnDefinitions: ColumnDefinition[];
  companies: SelectionItem<string>[]; // value is companyId
  dataContext: ExpandedListViewItemDefinition[];
  form: FormGroup;

  ngOnInit() {
    this._dimensionLabelsService.getDefaultLabels().subscribe(labels => (this.dimensionLabels = labels));

    this._companyService.getList().subscribe(data => {
      this.companies = data.map(item => new SelectionItem(item.toString(), item.id));

      if (this.companies.length) {
        this.form.controls['companyId'].setValue(this.companies[0].value);
      }
    });

    this.form.controls['companyId'].valueChanges.subscribe(companyId => {
      this._dimensionLabelsService.getForCompany(companyId).subscribe(labels => (this.dimensionLabels = labels));
    });
  }

  onInvoiceCaptionClicked(e: RowClickEvent<ExpandedListViewRow>) {
    this._navigationService.navigate([AccountRoutes.invoices, 'edit', e.context.dataSource.invoiceId], null, true);
  }

  exportExcel() {
    if (!canSubmitReportForm(this.form)) return;
    this._dataService.getAccountBalancePerInvoiceExcel(this.form.value).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;

    this.sums = {};

    this._dataService.getAccountBalancePerInvoice(this.form.value).subscribe(data => {
      const accountBalance = this.calculateSum(data, 'balance');
      this.sums.accountBalanceCurrency = accountBalance.currency;
      this.sums.accountBalanceSum = accountBalance.sum;
      this.sums.isAccountBalanceSumAvailable = accountBalance.isAvailable;

      const debit = this.calculateSum(data, 'debit');
      this.sums.debitCurrency = debit.currency;
      this.sums.debitSum = debit.sum;
      this.sums.isDebitSumAvailable = debit.isAvailable;

      const credit = this.calculateSum(data, 'credit');
      this.sums.creditCurrency = credit.currency;
      this.sums.creditSum = credit.sum;
      this.sums.isCreditSumAvailable = credit.isAvailable;

      this.dataContext = data.map(overview => {
        const model = new ExpandedListViewItemDefinition();
        model.caption = overview.invoiceNumber;
        model.columnDefinitions = [
          this._cdf.create('VERIFICATION.NUMBER', 'verificationNumber', { allowSort: AllowSort.No }),
          this._cdf.create('VERIFICATION.DESCRIPTION', 'verificationDescription', { allowSort: AllowSort.No }),
          this._cdf.create('VERIFICATION.CREATED_DATE', 'creationDate', { allowSort: AllowSort.No }),
          this._cdf.create('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate', { allowSort: AllowSort.No }),
          this._cdf.create('ACCOUNT.NAME', 'accountName', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
          this._cdf.create('ACCOUNT.NUMBER', 'accountNumber', { allowSort: AllowSort.No }),
          this._cdf.create('ACCOUNT.DEBIT', 'debit', { allowSort: AllowSort.No }),
          this._cdf.create('ACCOUNT.CREDIT', 'credit', { allowSort: AllowSort.No }),
          this._cdf.create(this.dimensionLabels.costPlaceLabel, 'costPlace', { allowSort: AllowSort.No }),
          this._cdf.create(this.dimensionLabels.costBearerLabel, 'costBearer', {
            allowSort: AllowSort.No,
            visibility: ColumnVisibility.optional,
          }),
          this._cdf.create(this.dimensionLabels.counterpartLabel, 'counterpart', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
          this._cdf.create(this.dimensionLabels.projectLabel, 'project', { allowSort: AllowSort.No }),
        ];
        model.dataContext = overview;
        model.icon = 'account-statement';
        model.dataLoader = dataContext => this._invoiceService.getVerifications(dataContext.invoiceId);
        return model;
      });
    });
  }

  private calculateSum(
    data: any[],
    property: 'balance' | 'debit' | 'credit'
  ): { currency: string | null; sum: number; isAvailable: boolean } {
    const currencies = data.map(x => x[property]?.currencyCode).filter(Boolean);
    const uniqueCurrencies = Array.from(new Set(currencies));
    if (uniqueCurrencies.length === 1) {
      return {
        currency: uniqueCurrencies[0],
        sum: data.reduce((sum, overview) => sum + (overview[property]?.amount ?? 0), 0),
        isAvailable: true,
      };
    }
    return { currency: null, sum: 0, isAvailable: false };
  }
}
